import React, { useEffect, useMemo, useState } from "react";
import { Pressable, ScrollView, StyleSheet, View } from "react-native";
import { PlusIcon, TrashIcon, XIcon } from "@phosphor-icons/react";
import { GradebookActivityType } from "@axio/graphql/generated/graphql";

import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomModal from "../../common/general/CustomModal/CustomModal";
import CustomSwitch from "../../common/general/CustomSwitch/CustomSwitch";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomTextInput from "../../common/general/CustomTextInput/CustomTextInput";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import type { DraftWeightingCategory, DraftWeightingConfig, WeightingActivity } from "./gradebookWeighting.types";
import {
  calculateWeightTotal,
  createDefaultWeightingCategories,
  createLocalCategoryId,
  isActivityInCategory,
} from "./gradebookWeighting.utils";

interface WeightingDialogProps {
  visible: boolean;
  config: DraftWeightingConfig;
  activities: WeightingActivity[];
  saving?: boolean;
  errorMessage?: string | null;
  onClose: () => void;
  onSave: (config: DraftWeightingConfig) => void;
}

const activityTypeLabel = (activityType: GradebookActivityType): string => {
  if (activityType === GradebookActivityType.Assignment) return "Assignment";
  if (activityType === GradebookActivityType.Assessment) return "Assessment";
  return "Project";
};

const updateActivityIds = (
  category: DraftWeightingCategory,
  activity: WeightingActivity,
  updater: (ids: string[]) => string[]
): DraftWeightingCategory => {
  if (activity.activityType === GradebookActivityType.Assignment) {
    return { ...category, assignmentIds: updater(category.assignmentIds) };
  }
  if (activity.activityType === GradebookActivityType.Assessment) {
    return { ...category, assessmentIds: updater(category.assessmentIds) };
  }
  return { ...category, projectIds: updater(category.projectIds) };
};

const getAssignedActivityIds = (category: DraftWeightingCategory): string[] => [
  ...category.assignmentIds,
  ...category.assessmentIds,
  ...category.projectIds,
];

function WeightingDialog({
  visible,
  config,
  activities,
  saving = false,
  errorMessage,
  onClose,
  onSave,
}: WeightingDialogProps) {
  const [draft, setDraft] = useState<DraftWeightingConfig>(config);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setDraft(config);
      setPendingDeleteId(null);
    }
  }, [config, visible]);

  const totalWeight = useMemo(() => calculateWeightTotal(draft), [draft]);
  const isValid = !draft.enabled || Math.abs(totalWeight - 100) < 0.01;

  const assignedActivityIds = useMemo(() => {
    const ids = new Set<string>();
    draft.categories.forEach((category) => {
      getAssignedActivityIds(category).forEach((activityId) => ids.add(activityId));
    });
    return ids;
  }, [draft.categories]);

  const unassignedActivities = useMemo(
    () => activities.filter((activity) => !assignedActivityIds.has(activity.activityId)),
    [activities, assignedActivityIds]
  );

  const updateCategory = (categoryId: string, updates: Partial<DraftWeightingCategory>) => {
    setDraft((prev) => ({
      ...prev,
      categories: prev.categories.map((category) =>
        category.id === categoryId ? { ...category, ...updates } : category
      ),
    }));
  };

  const addCategory = () => {
    setDraft((prev) => ({
      ...prev,
      categories: [
        ...prev.categories,
        {
          id: createLocalCategoryId(),
          name: "New Category",
          weight: 0,
          sortOrder: prev.categories.length,
          assignmentIds: [],
          assessmentIds: [],
          projectIds: [],
        },
      ],
    }));
  };

  const removeCategory = (categoryId: string) => {
    setDraft((prev) => ({
      ...prev,
      categories: prev.categories
        .filter((category) => category.id !== categoryId)
        .map((category, index) => ({ ...category, sortOrder: index })),
    }));
    setPendingDeleteId(null);
  };

  const assignActivity = (activity: WeightingActivity, categoryId: string) => {
    setDraft((prev) => ({
      ...prev,
      categories: prev.categories.map((category) => {
        const withoutActivity = updateActivityIds(category, activity, (ids) =>
          ids.filter((activityId) => activityId !== activity.activityId)
        );
        if (category.id !== categoryId) return withoutActivity;
        return updateActivityIds(withoutActivity, activity, (ids) => [...ids, activity.activityId]);
      }),
    }));
  };

  const unassignActivity = (activity: WeightingActivity) => {
    setDraft((prev) => ({
      ...prev,
      categories: prev.categories.map((category) =>
        updateActivityIds(category, activity, (ids) => ids.filter((activityId) => activityId !== activity.activityId))
      ),
    }));
  };

  const setWeightedMode = (enabled: boolean) => {
    setDraft((prev) => ({
      ...prev,
      enabled,
      categories:
        enabled && prev.categories.length === 0
          ? createDefaultWeightingCategories(activities, createLocalCategoryId)
          : prev.categories,
    }));
  };

  const renderActivityChip = (activity: WeightingActivity, category?: DraftWeightingCategory) => (
    <View key={activity.activityId} style={styles.activityChip}>
      <View style={styles.activityChipText}>
        <CustomText
          text={activity.activityName}
          size="xs"
          weight="medium"
          numberOfLines={1}
          useTranslationText={false}
          style={styles.activityName}
        />
        <CustomText
          text={`${activity.moduleName} · ${activityTypeLabel(activity.activityType)}`}
          size="xs"
          useTranslationText={false}
          style={styles.activityMeta}
        />
      </View>
      {category ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`Remove ${activity.activityName} from ${category.name}`}
          onPress={() => unassignActivity(activity)}
          style={styles.iconButton}>
          <XIcon size={14} color="#667085" />
        </Pressable>
      ) : null}
    </View>
  );

  return (
    <CustomModal
      visible={visible}
      onRequestClose={onClose}
      title="Grade Weighting"
      showCloseButton
      ariaLabel="Grade weighting"
      style={styles.modal}
      bodyStyle={styles.modalBody}>
      <CustomText
        text="Configure categories and assign activities. Unassigned activities do not count toward the final grade."
        size="s"
        useTranslationText={false}
        style={styles.description}
      />
      <CustomSpacing size="m" type="vertical" />

      <View style={styles.toggleRow}>
        <View style={styles.toggleCopy}>
          <CustomText text="Enable Weighted Grading" size="s" weight="semibold" useTranslationText={false} />
          <CustomText
            text="Weights must total 100% before saving."
            size="xs"
            useTranslationText={false}
            style={styles.mutedText}
          />
        </View>
        <CustomSwitch
          value={draft.enabled}
          onValueChange={setWeightedMode}
          accessibilityLabel="Enable weighted grading"
        />
      </View>

      {draft.enabled ? (
        <>
          <CustomSpacing size="m" type="vertical" />
          <ScrollView style={styles.categoryList} contentContainerStyle={styles.categoryListContent}>
            {draft.categories.map((category) => {
              const assignedActivities = activities.filter((activity) =>
                isActivityInCategory(category, activity.activityId, activity.activityType)
              );
              const categoryPoints = assignedActivities.reduce((sum, activity) => sum + activity.maxPoints, 0);
              const confirmingDelete = pendingDeleteId === category.id;

              return (
                <View key={category.id} style={styles.categoryCard}>
                  <View style={styles.categoryHeader}>
                    <View style={styles.categoryNameInput}>
                      <CustomTextInput
                        value={category.name}
                        onChangeText={(name: string) => updateCategory(category.id, { name })}
                        placeholder="Category name"
                        accessibilityLabel="Category name"
                      />
                    </View>
                    <View style={styles.weightInputGroup}>
                      <CustomTextInput
                        value={String(category.weight)}
                        onChangeText={(value: string) => {
                          const weight = Number(value);
                          updateCategory(category.id, { weight: Number.isFinite(weight) ? weight : 0 });
                        }}
                        keyboardType="numeric"
                        placeholder="0"
                        accessibilityLabel={`${category.name} weight percentage`}
                        style={styles.weightInput}
                      />
                      <CustomText text="%" size="s" useTranslationText={false} style={styles.percentText} />
                    </View>
                    {draft.categories.length > 1 ? (
                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel={`Delete ${category.name}`}
                        onPress={() =>
                          assignedActivities.length > 0 ? setPendingDeleteId(category.id) : removeCategory(category.id)
                        }
                        style={styles.deleteButton}>
                        <TrashIcon size={18} color="#B42318" />
                      </Pressable>
                    ) : null}
                  </View>

                  {confirmingDelete ? (
                    <View style={styles.deleteConfirm}>
                      <CustomText
                        text={`Remove category? ${assignedActivities.length} ${
                          assignedActivities.length === 1 ? "activity" : "activities"
                        } will become uncategorized.`}
                        size="xs"
                        useTranslationText={false}
                        style={styles.deleteConfirmText}
                      />
                      <View style={styles.deleteConfirmActions}>
                        <CustomButton
                          text="Cancel"
                          size="xs"
                          styleType="transparent"
                          onPress={() => setPendingDeleteId(null)}
                          useTranslationText={false}
                        />
                        <CustomButton
                          text="Remove"
                          size="xs"
                          styleType="danger"
                          onPress={() => removeCategory(category.id)}
                          useTranslationText={false}
                        />
                      </View>
                    </View>
                  ) : null}

                  <CustomText
                    text={
                      assignedActivities.length > 0
                        ? `${assignedActivities.length} ${
                            assignedActivities.length === 1 ? "activity" : "activities"
                          } · ${categoryPoints} pts`
                        : "No activities assigned"
                    }
                    size="xs"
                    useTranslationText={false}
                    style={styles.categorySummary}
                  />

                  <View style={styles.chipWrap}>
                    {assignedActivities.map((activity) => renderActivityChip(activity, category))}
                  </View>

                  {unassignedActivities.length > 0 ? (
                    <View style={styles.addActivityRow}>
                      <CustomText
                        text="Add"
                        size="xs"
                        weight="semibold"
                        useTranslationText={false}
                        style={styles.addLabel}
                      />
                      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                        <View style={styles.addChipWrap}>
                          {unassignedActivities.map((activity) => (
                            <Pressable
                              key={activity.activityId}
                              accessibilityRole="button"
                              accessibilityLabel={`Add ${activity.activityName} to ${category.name}`}
                              onPress={() => assignActivity(activity, category.id)}
                              style={styles.addChip}>
                              <PlusIcon size={12} color="#3E68FE" />
                              <CustomText
                                text={activity.activityName}
                                size="xs"
                                numberOfLines={1}
                                useTranslationText={false}
                                style={styles.addChipText}
                              />
                            </Pressable>
                          ))}
                        </View>
                      </ScrollView>
                    </View>
                  ) : null}
                </View>
              );
            })}
          </ScrollView>

          <View style={styles.totalRow}>
            <CustomText
              text="Total"
              size="s"
              weight="semibold"
              useTranslationText={false}
              style={isValid ? styles.validText : styles.invalidText}
            />
            <CustomText
              text={`${totalWeight}%`}
              size="s"
              weight="semibold"
              useTranslationText={false}
              style={isValid ? styles.validText : styles.invalidText}
            />
          </View>

          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Add category"
            onPress={addCategory}
            style={styles.addCategoryButton}>
            <PlusIcon size={16} color="#3E68FE" />
            <CustomText
              text="Add Category"
              size="s"
              weight="semibold"
              useTranslationText={false}
              style={styles.addCategoryText}
            />
          </Pressable>

          {unassignedActivities.length > 0 ? (
            <View style={styles.uncategorizedSection}>
              <CustomText
                text={`Uncategorized activities (${unassignedActivities.length})`}
                size="xs"
                weight="semibold"
                useTranslationText={false}
                style={styles.uncategorizedTitle}
              />
              <View style={styles.chipWrap}>
                {unassignedActivities.map((activity) => (
                  <View key={activity.activityId} style={styles.uncategorizedItem}>
                    {renderActivityChip(activity)}
                    <View style={styles.assignButtons}>
                      {draft.categories.map((category) => (
                        <CustomButton
                          key={category.id}
                          text={category.name}
                          size="xs"
                          styleType="primaryTransparentStroke"
                          onPress={() => assignActivity(activity, category.id)}
                          useTranslationText={false}
                          textProps={{ numberOfLines: 1 }}
                          style={styles.assignButton}
                        />
                      ))}
                    </View>
                  </View>
                ))}
              </View>
            </View>
          ) : null}
        </>
      ) : null}

      {errorMessage ? (
        <CustomText text={errorMessage} size="xs" useTranslationText={false} style={styles.errorText} />
      ) : null}

      <View style={styles.footer}>
        <CustomButton text="Cancel" size="s" styleType="lightStroke" onPress={onClose} useTranslationText={false} />
        <CustomButton
          text={saving ? "Saving..." : "Save"}
          size="s"
          styleType="primary"
          onPress={() => onSave(draft)}
          disabled={saving || !isValid}
          useTranslationText={false}
        />
      </View>
    </CustomModal>
  );
}

export default WeightingDialog;

const styles = StyleSheet.create({
  modal: {
    width: 640,
  },
  modalBody: {
    maxHeight: 720,
  },
  description: {
    color: "#475467",
    lineHeight: 20,
  },
  toggleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },
  toggleCopy: {
    flex: 1,
  },
  mutedText: {
    color: "#667085",
    marginTop: 4,
  },
  categoryList: {
    maxHeight: 380,
  },
  categoryListContent: {
    gap: 12,
  },
  categoryCard: {
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 8,
    backgroundColor: "#FCFCFD",
    padding: 12,
    gap: 10,
  },
  categoryHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  categoryNameInput: {
    flex: 1,
  },
  weightInputGroup: {
    width: 96,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  weightInput: {
    textAlign: "center",
  },
  percentText: {
    color: "#475467",
  },
  deleteButton: {
    width: 34,
    height: 34,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  deleteConfirm: {
    borderWidth: 1,
    borderColor: "#FECDCA",
    backgroundColor: "#FEF3F2",
    borderRadius: 8,
    padding: 10,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  deleteConfirmText: {
    flex: 1,
    color: "#B42318",
  },
  deleteConfirmActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  categorySummary: {
    color: "#667085",
  },
  chipWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  activityChip: {
    maxWidth: 230,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
    paddingHorizontal: 8,
    paddingVertical: 6,
  },
  activityChipText: {
    minWidth: 0,
    flex: 1,
  },
  activityName: {
    color: "#344054",
  },
  activityMeta: {
    color: "#98A2B3",
    marginTop: 2,
  },
  iconButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  addActivityRow: {
    borderTopWidth: 1,
    borderTopColor: "#EAECF0",
    paddingTop: 10,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  addLabel: {
    color: "#667085",
  },
  addChipWrap: {
    flexDirection: "row",
    gap: 8,
  },
  addChip: {
    maxWidth: 180,
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderRadius: 8,
    backgroundColor: "#EEF4FF",
  },
  addChipText: {
    color: "#3E68FE",
  },
  totalRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 10,
  },
  validText: {
    color: "#027A48",
  },
  invalidText: {
    color: "#B42318",
  },
  addCategoryButton: {
    alignSelf: "flex-start",
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 8,
  },
  addCategoryText: {
    color: "#3E68FE",
  },
  uncategorizedSection: {
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: "#D0D5DD",
    borderRadius: 8,
    padding: 12,
    backgroundColor: "#F9FAFB",
    gap: 10,
  },
  uncategorizedTitle: {
    color: "#475467",
  },
  uncategorizedItem: {
    gap: 8,
  },
  assignButtons: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  assignButton: {
    maxWidth: 160,
  },
  errorText: {
    color: "#B42318",
    marginTop: 12,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 18,
  },
});
